import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import CloseIcon from '@mui/icons-material/Close'

import TextInputController from '@/components/ui/inputs/TextInputController'
import { useEffect } from 'react'

const biologicalValidationSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
  unit: z.string().min(1),
  comment: z.string().optional(),
})

export type AddBiologicalFormValues = z.infer<typeof biologicalValidationSchema>

export type AddBiologicalsFormProps = {
  open: boolean
  onClose: () => void
  onSubmit: (values: AddBiologicalFormValues) => void
  loading: boolean
}

function AddBiologicalDialog(props: AddBiologicalsFormProps) {
  const { open, onClose, onSubmit, loading } = props
  const { t } = useTranslation()

  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
  } = useForm<AddBiologicalFormValues>({ defaultValues: { label: '', comment: '', unit: '', value: '' }, resolver: zodResolver(biologicalValidationSchema) })

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('component.group.biologicals.addBiologicalButton.label')}
        <IconButton
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing="20px" alignContent="left">
            <TextInputController control={control} fullWidth label={`${t('component.group.biologicals.field.label')}`} name="label" />
            <Stack direction="row" spacing="20px">
              <TextInputController control={control} fullWidth label={`${t('component.group.biologicals.field.value')}`} name="value" />
              <TextInputController control={control} fullWidth label={`${t('component.group.biologicals.field.unit')}`} name="unit" />
            </Stack>
            <TextInputController control={control} multiline rows={'4'} fullWidth label={`${t('component.group.biologicals.field.comment')}`} name="comment" />
            <LoadingButton variant="contained" disabled={!isValid} loading={loading} type="submit">
              {t('common:button.add.label')}
            </LoadingButton>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddBiologicalDialog
